import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { AuthService } from '@core/services/auth/auth.service';
import {
  PalantirButtonComponent,
  PalantirDialogComponent,
  PalantirDialogRef,
  PalantirDialogService,
  PalantirTextComponent,
  PalantirToastService,
} from '@flux/design-system';
import { OverridePermissionsComponent } from './components/override-permissions/override-permissions.component';
import { DebugLogsConfiguratorComponent } from './components/debug-logs-configurator/debug-logs-configurator';
import { DebugService } from '@core/services/debug/debug.service';
import { SyncService } from '@core/services/sync.service';
import { Router } from '@angular/router';
import { environment } from '@environment';
import { Apollo } from 'apollo-angular';
import { RefreshAuthService } from '@core/services/auth/refresh-auth.service';
import { ErrorHandlerService } from '@core/services/error/error.service';
import { BoardAutomationDisplayService } from '@/app/pages/flux/views/board/components/board-automation-display/board-automation-display.service';
import { RealtimeService } from '@/app/core/services/realtime/realtime.service';
import { FeatureFlagService } from '@/app/core/services/feature-flag/feature-flag.service';

@Component({
  selector: 'app-debug-menu',
  imports: [CommonModule, PalantirDialogComponent, PalantirButtonComponent, PalantirTextComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './debug-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebugMenuComponent {
  public readonly MENUS: { title: string; description: string; action: () => void }[] = [
    {
      title: 'Logar Informações do Usuário',
      description: 'Loga as informações do usuário no console.',
      action: () => {
        this.toastService.showInfo('Informações do usuário logadas no console.');
      },
    },
    {
      title: 'Deslogar Usuário',
      description: 'Desloga o usuário atual.',
      action: () => {
        this.authService.logout();
        this.toastService.showInfo('Usuário deslogado com sucesso.');
      },
    },
    {
      title: 'Override de permissões',
      description:
        'Permite sobrescrever as permissões do cliente para testar permissões. O servidor ainda ira validar suas permissões reais.',
      action: () => {
        this.dialogService.openDialog(OverridePermissionsComponent, {
          size: 'medium',
        });
      },
    },
    {
      title: 'Configurar Logs de Debug',
      description: 'Habilita ou desabilita logs de debug via namespaces e wildcards.',
      action: () => {
        this.dialogService.openDialog(DebugLogsConfiguratorComponent, {
          size: 'medium',
        });
      },
    },
    {
      title: 'Limpar metadados de debug',
      description:
        'Remove todos os metadados de debug, isso vai remover qualquer informação de debug que tenha sido salva. NAO AFETA LOGS DE DEBUG.',
      action: () => {
        this.debugService.clearDebugMetadata();
        this.toastService.showWarning('Metadados de debug limpos com sucesso.');
      },
    },
    {
      title: 'Gerar relatório de debug completo',
      description: 'Gera um relatório de debug completo no console.',
      action: () => {
        this.generateFullDebugReport();
        this.toastService.showWarning('Relatório de debug gerado no console.');
      },
    },
    {
      title: 'Sincronizar cargo',
      description:
        'Sincroniza o cargo do usuário com o servidor. (normalmente acontece a cada 5 minutos)',
      action: () => {
        this.refreshAuthService.refreshAuthenticatedUser();
        this.toastService.showInfo('Sincronização de cargo iniciada.');
      },
    },
    {
      title: 'Toggle persisted queries',
      description:
        'Ativa ou desativa as persisted queries para requisições GraphQL. Por padrão está ativado.',
      action: () => {
        this.togglePersistedQueries();
        this.toastService.showInfo('Persisted queries toggled.');
      },
    },
  ];

  public constructor(
    private readonly dialogRef: PalantirDialogRef,
    private dialogService: PalantirDialogService,
    private readonly toastService: PalantirToastService,
    private authService: AuthService,
    private readonly debugService: DebugService,
    private readonly syncService: SyncService,
    private readonly router: Router,
    private readonly apollo: Apollo,
    private readonly refreshAuthService: RefreshAuthService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly boardAutomationDisplayService: BoardAutomationDisplayService,
    private readonly realtimeService: RealtimeService,
    private readonly featureFlagService: FeatureFlagService,
  ) {}

  public close(): void {
    this.dialogRef.close();
  }

  public generateFullDebugReport(): void {
    const report = {
      router: {
        url: this.router.url,
        state: this.router.getCurrentNavigation()?.extras.state,
        config: this.router.config,
      },
      environment: environment,
      debugMetadata: this.debugService.getDebugMetadata(),
      authenticatedUser: this.authService.authenticatedUser,
      apollo: {
        cache: this.apollo.client.cache.extract(),
        memoryInternals: this.apollo.client.getMemoryInternals?.(),
        defaultOptions: this.apollo.client.defaultOptions,
      },
      authService: this.authService.generateDebugReport(),
      syncService: this.syncService.generateDebugReport(),
      refreshAuthService: this.refreshAuthService.generateDebugReport(),
      errorHandlerService: this.errorHandlerService.generateDebugReport(),
      boardAutomationDisplayService: this.boardAutomationDisplayService.generateDebugReport(),
      performance: this.generatePerformanceReport(),
      webSocket: this.realtimeService.generateDebugReport(),
      featureFlag: this.featureFlagService.generateDebugReport(),
      browser: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        // @ts-expect-error: Non-standard
        networkInformation: navigator.connection,
      },
    };

    try {
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      window.open(url);
    } catch (error) {
      console.error('Error downloading debug report', error);
    }
  }

  private generatePerformanceReport() {
    return {
      // @ts-expect-error: Deprecated
      memory: performance.memory,
      navigation: performance.navigation,
      timing: performance.timing,
      measure: performance.measure(`debug-${this.generateRandomId()}`, {}),
    };
  }

  generateRandomId(): string {
    return Math.random().toString(36).substring(7);
  }

  private togglePersistedQueries(): void {
    const currentSetting =
      this.debugService.getSingleDebugMetadata<boolean>('persistedQueriesEnabled');

    const newSetting = !(currentSetting ?? true);

    this.debugService.setDebugMetadata('persistedQueriesEnabled', newSetting);
    window.location.reload();
  }
}
